import React, { useEffect, useState } from "react";
import { Star, Loader2, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { month: "long", year: "numeric" }) : "");

export const StarRow = ({ rating, className = "w-4 h-4" }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${className} ${i <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} aria-hidden />
    ))}
  </span>
);

export default function TutorReviews({ tutorId, tutorName }) {
  const [reviews, setReviews] = useState([]);
  const [f, setF] = useState({ first_name: "", email: "", year_level: "", rating: 5, comment: "" });
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/tutors/${tutorId}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tutorId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim() || !f.email.trim() || !f.comment.trim()) return toast.error("Please add your name, email and review.");
    setSending(true);
    try {
      await api.post(`/tutors/${tutorId}/reviews`, { ...f, rating: Number(f.rating) });
      toast.success("Thank you! Your review has been sent for approval and will appear shortly.");
      setF({ first_name: "", email: "", year_level: "", rating: 5, comment: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send that review. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-12" data-testid="tutor-reviews">
      <h2 className="font-display text-2xl font-bold text-slate-900">
        {reviews.length > 0 ? `What families say about ${tutorName}` : "Reviews"}
      </h2>

      {reviews.length > 0 ? (
        <div className="space-y-4 mt-5">
          {reviews.map((r) => (
            <Card key={r.id} className="p-5 border-slate-200" data-testid={`tutor-review-${r.id}`}>
              <StarRow rating={r.rating} />
              <p className="font-serif-editorial italic text-slate-700 mt-3 leading-relaxed">“{r.comment}”</p>
              <p className="text-sm text-slate-500 mt-3">
                {r.first_name}{r.year_level ? ` · ${r.year_level}` : ""} · {fmt(r.created_at)}
              </p>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500 mt-2" data-testid="tutor-reviews-empty">
          No reviews yet. If you've had a session with {tutorName}, you can be the first.
        </p>
      )}

      <Card className="p-6 border-slate-200 mt-6" data-testid="tutor-review-form-card">
        <h3 className="font-display font-semibold text-slate-900">Leave a review</h3>
        <p className="text-sm text-slate-500 mt-1">
          Reviews are open to families who have booked through Teaching Pathways — please use the same email address as
          your booking enquiry. Every review is read before it goes live.
        </p>
        <form onSubmit={submit} className="space-y-3 mt-5" data-testid="tutor-review-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label htmlFor="rv-name">First name</Label><Input id="rv-name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="mt-1.5" data-testid="review-first-name" /></div>
            <div><Label htmlFor="rv-email">Email used to book</Label><Input id="rv-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" data-testid="review-email" /></div>
            <div><Label htmlFor="rv-year">Student year level</Label><Input id="rv-year" value={f.year_level} onChange={(e) => setF({ ...f, year_level: e.target.value })} placeholder="Year 8" className="mt-1.5" data-testid="review-year-level" /></div>
          </div>
          <div>
            <Label className="block mb-2">Rating</Label>
            <div className="flex items-center gap-1" data-testid="review-star-picker">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} type="button" onClick={() => setF({ ...f, rating: i })} aria-label={`${i} stars`} data-testid={`review-star-${i}`}>
                  <Star className={`w-7 h-7 transition-colors ${i <= f.rating ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"}`} />
                </button>
              ))}
            </div>
          </div>
          <div><Label htmlFor="rv-comment">Your review</Label><Textarea id="rv-comment" rows={4} value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} className="mt-1.5" placeholder="How did the sessions help your child?" data-testid="review-comment" /></div>
          <Button type="submit" disabled={sending} className="bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="review-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Submit review</>}
          </Button>
        </form>
      </Card>
    </div>
  );
}
